// How a ticket prints: labels, money, times, chance. One place, so Today,
// Ticket and History cannot drift into three ways of writing $12.40.

export const TIER_LABEL = {
  low: 'Low risk',
  medium: 'Medium risk',
  high: 'High risk',
  outrageous: 'Outrageous',
};

/** Tier ink comes from the multi's tier, resolved against the tokens in app.css. */
export const tierInk = (tier) => `var(--tier-${tier}, var(--carbon))`;

/** Prices older than this carry a STALE stamp. */
export const STALE_AFTER_MIN = 30;

export const isStale = (fetchedAt, now = Date.now()) =>
  !!fetchedAt && now - new Date(fetchedAt).getTime() > STALE_AFTER_MIN * 60 * 1000;

const AUD = new Intl.NumberFormat('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * The em dash as a GLYPH, not punctuation: the cell with no number in it, the
 * leg that was voided, the day that moved neither way.
 *
 * It is the only em dash the app is allowed to print — the copy rule is no em
 * dashes in a sentence, and `copy-rules.test.js` enforces it by failing on the
 * character anywhere but here. So this is the one place it is typed, and a
 * screen that needs a blank cell imports it rather than writing it out. Same
 * argument as `money`: one definition, so five screens cannot drift into five
 * ways of printing nothing.
 */
export const NO_VALUE = '—';

/** $12.40, $1,026.51, −$40.00, or the no-value glyph when there is no number. */
export function money(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return NO_VALUE;
  const v = Number(n);
  return v < 0 ? `−$${AUD.format(-v)}` : `$${AUD.format(v)}`;
}

/**
 * The same money with its direction on the front: "+$12.40", "−$8.00",
 * "$0.00", or the no-value glyph when there is no number.
 *
 * A net figure without a sign is a size, not a result — the same argument
 * `signedPct` is written on — and the minus is `money`'s own U+2212 rather than
 * a hyphen, because the two print side by side in a column of figures. Nothing
 * is added to a zero: "+$0.00" claims a direction a flat day does not have.
 *
 * Three screens had grown their own one-liner for this (BacktestResultPage,
 * HedgeCard, and next the Insights tab), which is exactly the drift the top of
 * this file exists to stop.
 */
export function signedMoney(n) {
  const v = Number(n);
  if (n === null || n === undefined || !Number.isFinite(v)) return NO_VALUE;
  // `v || 0` is the negative zero, flattened: a net of exactly nothing arrives
  // as -0 whenever it is a subtraction that came out even, and `money` would
  // print it "$-0.00" with a hyphen that belongs to no number.
  return v > 0 ? `+${money(v)}` : money(v || 0);
}

export const price = (n) => (Number.isFinite(n) ? n.toFixed(2) : NO_VALUE);

/**
 * 9:12am — no space before the meridiem, matching Today's own clock
 * (`components/ask/*`, `lib/ask/chips.js`). Intl's en-AU format prints a space
 * there; stripping it is the one thing that keeps every screen's timestamp
 * reading the same way. `timeZone` is device zone when omitted — only
 * `lib/when.js`'s tests pass one.
 */
export const time = (iso, timeZone) =>
  new Date(iso).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', timeZone }).replace(' ', '');

/**
 * Saturday 12 September. A bare "YYYY-MM-DD" (a multi's forDate) is a local
 * calendar day — `new Date()` would read it as UTC midnight and print the day
 * before anywhere west of Greenwich.
 */
export const day = (when) =>
  new Date(/^\d{4}-\d{2}-\d{2}$/.test(when) ? `${when}T00:00:00` : when).toLocaleDateString('en-AU', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

/**
 * Sat 13 Sep — the same day, printed for a place that has a row's worth of
 * paper rather than a heading's.
 *
 * History's table prints the day beside the strategy on a 12px line, where
 * "Saturday 13 September" is 21 characters spent on a field nobody is scanning
 * for. It lives here rather than in the page for the reason `day` does: two
 * ways of writing the same Saturday is how a pad starts disagreeing with
 * itself, and the local-calendar-day care below is the part that is easy to
 * get wrong twice.
 */
export const dayShort = (when, timeZone) =>
  new Date(/^\d{4}-\d{2}-\d{2}$/.test(when) ? `${when}T00:00:00` : when).toLocaleDateString('en-AU', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone,
  });

/**
 * A probability as a percentage: "62.4%", or the no-value glyph when there is none.
 * One decimal, because the second one is noise on a devigged price.
 */
export const pct = (p) => (Number.isFinite(p) ? `${(p * 100).toFixed(1)}%` : NO_VALUE);

/**
 * An edge, with its sign: "+3.2%", "−1.8%", "0.0%". The sign is the whole
 * point — an edge without one reads as a size, not a direction — and the minus
 * is U+2212, the same one `money` uses, not a hyphen.
 */
export function signedPct(p) {
  if (!Number.isFinite(p)) return NO_VALUE;
  const body = `${(Math.abs(p) * 100).toFixed(1)}%`;
  if (p > 0) return `+${body}`;
  return p < 0 ? `−${body}` : body;
}

/**
 * The risk in words, always beside the dots: "1 in 12 chance" under a coin
 * toss, "62% chance" over it.
 *
 * NEVER "near certain". That was a rounding — `1/p` fell to 1 anywhere past
 * about 67% — and it printed a promise no price makes, on the one ticket a
 * punter is least likely to read twice. Past a coin toss the percentage is the
 * plainer reading anyway: "1 in 1 chance" is not English, and "1 in 2" is the
 * last rung that still reads as odds rather than as a claim.
 */
export function oneIn(p) {
  if (p === null || p === undefined || !(p > 0)) return 'chance unknown';
  if (p >= 0.5) return `${Math.round(p * 100)}% chance`;
  return `1 in ${Math.round(1 / p).toLocaleString('en-AU')} chance`;
}

/**
 * The chance with the one thing it assumes: "1 in 12 chance · legs assumed
 * unrelated".
 *
 * The Terms say a ticket's chance is worked out from the bookmaker's prices AND
 * treats the legs as unrelated, and the second half is the half that makes the
 * figure optimistic. A ticket that carries the number has to carry the caveat
 * with it — a feed is read one ticket at a time, and a screenshot leaves with
 * nothing but the ticket in it. Short form on purpose: it rides at the end of a
 * line that has already said the stake and the return.
 *
 * Only where there is a chance to qualify — "chance unknown · legs assumed
 * unrelated" is a caveat on nothing.
 */
export function chanceLine(p) {
  const chance = oneIn(p);
  return chance === 'chance unknown' ? chance : `${chance} · legs assumed unrelated`;
}
